import { Injectable } from "@angular/core";
import { ConfigService } from "./config.service";
import { AuthConfig, OAuthService } from "angular-oauth2-oidc";

export interface BasicUserInfo {
    sub: string;
    displayName: string;
    [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class AuthService {

    private initialized = false;
    
    constructor(
        private readonly oauthService: OAuthService,
        private readonly configService: ConfigService
    ) {}

    async init(): Promise<void> {
        if(this.initialized) {
            return
        }

        const cfg = this.configService.getConfig();
        const baseUrl = cfg.baseUrl.replace(/\/+$/, '') // NOSONAR - S4784: regex trivial que remove barras do final da URL, sem grupos aninhados ou backtracking exponencial
        const issuer = `${baseUrl}/oauth2/token`;

        const authConfig: AuthConfig = {
        issuer: issuer,
        clientId: cfg.clientID,
        redirectUri: cfg.signInRedirectURL,
        postLogoutRedirectUri: cfg.signOutRedirectURL,
        scope: Array.isArray(cfg.scope) ? cfg.scope.join(' ') : 'openid',
        responseType: 'code',
        oidc: true,

        loginUrl:         `${baseUrl}/oauth2/authorize`,
        tokenEndpoint:    `${baseUrl}/oauth2/token`,
        userinfoEndpoint: `${baseUrl}/oauth2/userinfo`,
        logoutUrl:        `${baseUrl}/oidc/logout`,
        revocationEndpoint: `${baseUrl}/oauth2/revoke`,
        };

        this.oauthService.configure(authConfig);

        if (cfg.periodicTokenRefresh) {
           this.oauthService.setupAutomaticSilentRefresh();
        }

        try {
        await this.oauthService.tryLoginCodeFlow();
        } catch (e) {
        console.error('Erro ao processar login code flow', e);
        }

        this.initialized = true;
    }

    isAuthenticated(): boolean {
        return this.oauthService.hasValidAccessToken();
    }

    async isAuthenticatedAsync(): Promise<boolean> {
        if (!this.initialized) {
            await this.init();
        }
        return this.isAuthenticated();
    }

    signIn(): void {
        this.oauthService.initLoginFlow();
    }

    async signOut(): Promise<void> {
        this.oauthService.logOut();
    }

    async refreshAccessToken(): Promise<void> {
        await this.oauthService.refreshToken();
    }

    async getAccessToken(): Promise<string> {
        return this.oauthService.getAccessToken();
    }

    async getBasicUserInfo(): Promise<BasicUserInfo> {
    const claims: any = this.oauthService.getIdentityClaims() ?? {};
    const fullName = (claims['given_name'] ?? '') + (claims['family_name'] ? ` ${claims['family_name']}` : '');
    return {
      sub: claims['sub'],
      displayName: 
        (fullName.trim() || claims['name']) ??
        claims['preferred_username'] ??
        claims['sub'],
      ...claims,
    };
}}